import { Layout } from "@/components/Layout";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Search, ClipboardCheck, IndianRupee, TrendingUp } from "lucide-react";
import { useState, useMemo } from "react";
import { DateRangeFilter } from "@/components/DateRangeFilter";
import { startOfToday, startOfYesterday, startOfMonth, format } from "date-fns";
import { useReceipts } from "@/lib/api/hooks/useReceipts";
import { authService } from "@/lib/api/services/auth.service";
import { cn } from "@/lib/utils";

// Modern premium stat card component
interface StatCardProps {
  title: string;
  value: React.ReactNode;
  icon: React.ElementType;
  variant?: "blue" | "emerald" | "violet";
  className?: string;
}

const StatCard = ({ title, value, icon: Icon, variant = "blue", className }: StatCardProps) => {
  const variants = {
    blue: {
      glow: "group-hover:shadow-[0_20px_50px_-12px_rgba(0,114,181,0.4)]",
      iconBg: "from-[#0072B5] via-[#0090B5] to-[#00B5B0]",
      accentLine: "from-[#0072B5] via-[#0090B5] to-[#00B5B0]",
      textGradient: "from-[#0072B5] to-[#00B5B0]",
      glowColor: "rgba(0,114,181,0.15)",
    },
    emerald: {
      glow: "group-hover:shadow-[0_20px_50px_-12px_rgba(16,185,129,0.4)]",
      iconBg: "from-emerald-500 via-emerald-600 to-teal-600",
      accentLine: "from-emerald-500 via-teal-500 to-cyan-500",
      textGradient: "from-emerald-500 to-teal-600",
      glowColor: "rgba(16,185,129,0.15)",
    },
    violet: {
      glow: "group-hover:shadow-[0_20px_50px_-12px_rgba(139,92,246,0.4)]",
      iconBg: "from-violet-500 via-purple-600 to-fuchsia-600",
      accentLine: "from-violet-500 via-purple-500 to-fuchsia-500",
      textGradient: "from-violet-500 to-fuchsia-600",
      glowColor: "rgba(139,92,246,0.15)",
    },
  };

  const config = variants[variant];

  return (
    <Card className={cn(
      "group relative overflow-hidden rounded-2xl",
      "bg-gradient-to-br from-background/95 via-background/90 to-muted/50",
      "backdrop-blur-xl",
      "border border-white/10 dark:border-white/5",
      "hover:border-white/20 dark:hover:border-white/10",
      "transition-all duration-500 ease-out",
      "hover:-translate-y-1",
      config.glow,
      className
    )}>
      {/* Animated gradient border */}
      <div className={cn(
        "absolute inset-0 rounded-2xl opacity-0 group-hover:opacity-100 transition-opacity duration-500",
        "bg-gradient-to-r p-[1px]",
        config.accentLine
      )}>
        <div className="absolute inset-[1px] rounded-2xl bg-background/95" />
      </div>
      
      {/* Background glow orbs */}
      <div 
        className="absolute -top-20 -right-20 w-40 h-40 rounded-full blur-3xl opacity-0 group-hover:opacity-60 transition-all duration-700"
        style={{ background: `radial-gradient(circle, ${config.glowColor}, transparent 70%)` }}
      />
      <div 
        className="absolute -bottom-10 -left-10 w-32 h-32 rounded-full blur-2xl opacity-0 group-hover:opacity-40 transition-all duration-700 delay-100"
        style={{ background: `radial-gradient(circle, ${config.glowColor}, transparent 70%)` }}
      />
      
      {/* Mesh pattern overlay */}
      <div className="absolute inset-0 opacity-[0.02] group-hover:opacity-[0.04] transition-opacity duration-500" 
        style={{
          backgroundImage: `radial-gradient(circle at 1px 1px, currentColor 1px, transparent 0)`,
          backgroundSize: '24px 24px'
        }}
      />
      
      <div className="relative p-5">
        <div className="flex items-start justify-between gap-4">
          {/* Icon with enhanced styling */}
          <div className="relative">
            {/* Icon glow */}
            <div 
              className={cn(
                "absolute inset-0 rounded-2xl blur-xl opacity-40 group-hover:opacity-70 transition-opacity duration-500",
                `bg-gradient-to-br ${config.iconBg}`
              )}
            />
            <div className={cn(
              "relative p-3.5 rounded-2xl",
              `bg-gradient-to-br ${config.iconBg}`,
              "shadow-lg",
              "ring-1 ring-white/25",
              "group-hover:scale-110 group-hover:rotate-[-6deg] transition-all duration-500 ease-out"
            )}>
              <Icon className="h-5 w-5 text-white drop-shadow-md" strokeWidth={2.5} />
            </div>
          </div>
          
          {/* Content */}
          <div className="flex-1 text-right">
            <p className="text-xs font-medium text-muted-foreground/70 uppercase tracking-wider mb-1">
              {title}
            </p>
            <div className={cn(
              "text-2xl font-bold tracking-tight",
              "bg-gradient-to-r bg-clip-text text-transparent",
              config.textGradient
            )}>
              {value}
            </div>
          </div>
        </div>
        
        {/* Bottom decorative element */}
        <div className="mt-4 flex items-center gap-2">
          <div className={cn(
            "flex-1 h-1 rounded-full bg-gradient-to-r opacity-20 group-hover:opacity-40 transition-opacity duration-500",
            config.accentLine
          )} />
          <div className={cn(
            "w-1.5 h-1.5 rounded-full bg-gradient-to-r",
            config.accentLine,
            "animate-pulse"
          )} />
        </div>
      </div>
    </Card>
  );
};

const Receipts = () => {
  const [search, setSearch] = useState("");
  const [dateFilter, setDateFilter] = useState<"today" | "yesterday" | "mtd" | "custom">("today");
  const [dateRange, setDateRange] = useState<{ from: Date | undefined; to: Date | undefined }>({ from: undefined, to: undefined });
  // 0 = ALL, 1 = Pending, 2 = Bill (default to Pending)
  const [status, setStatus] = useState<number>(1);
  const [selectedNarration, setSelectedNarration] = useState<{ text: string; receiptNo: number } | null>(null);
  const [isNarrationDialogOpen, setIsNarrationDialogOpen] = useState(false);

  // Get firmId from user
  const user = authService.getCurrentUser();
  const firmId = user?.firmId;

  // Calculate date range for API
  const { fromDate, toDate } = useMemo(() => {
    const now = new Date();
    let fromDate: Date;
    let toDate: Date = new Date(now.getFullYear(), now.getMonth(), now.getDate(), 23, 59, 59, 999);

    switch (dateFilter) {
      case "today":
        fromDate = startOfToday();
        break;
      case "yesterday":
        fromDate = startOfYesterday();
        toDate = new Date(fromDate.getFullYear(), fromDate.getMonth(), fromDate.getDate(), 23, 59, 59, 999);
        break;
      case "mtd":
        fromDate = startOfMonth(now);
        break;
      case "custom":
        if (dateRange.from && dateRange.to) {
          fromDate = new Date(dateRange.from);
          fromDate.setHours(0, 0, 0, 0);
          toDate = new Date(dateRange.to);
          toDate.setHours(23, 59, 59, 999);
        } else {
          fromDate = startOfToday();
        }
        break;
      default:
        fromDate = startOfToday();
    }

    // Format dates as ISO string with milliseconds and Z (UTC) to match API endpoint format
    return {
      fromDate: fromDate.toISOString(),
      toDate: toDate.toISOString(),
    };
  }, [dateFilter, dateRange]);

  // Fetch receipts from API
  const { data: receipts = [], isLoading, error } = useReceipts({
    firmId,
    fromDate,
    toDate,
    status,
  });

  const handleDateFilterChange = (
    filter: "today" | "yesterday" | "mtd" | "custom", 
    range?: { from: Date | undefined; to: Date | undefined }
  ) => {
    setDateFilter(filter);
    if (range) {
      setDateRange(range);
    }
  };

  const filteredReceipts = useMemo(() => {
    if (!receipts || !Array.isArray(receipts)) return [];
    
    // Filter by search only - date filtering is handled by API
    return receipts.filter(
      (receipt) =>
        receipt.receiptNo.toString().toLowerCase().includes(search.toLowerCase()) ||
        receipt.partyName.toLowerCase().includes(search.toLowerCase()) ||
        receipt.salesManCode.toLowerCase().includes(search.toLowerCase())
    );
  }, [receipts, search]);

  const getModeColor = (mode: string) => {
    switch (mode.toLowerCase()) {
      case "bank transfer":
      case "neft":
      case "rtgs":
        return "bg-primary text-primary-foreground";
      case "cheque":
        return "bg-warning text-warning-foreground";
      case "cash":
        return "bg-accent text-accent-foreground";
      case "upi":
        return "bg-blue-500 text-white";
      default:
        return "bg-secondary text-secondary-foreground";
    }
  };

  const formatDate = (dateString: string) => {
    try {
      const date = new Date(dateString);
      return format(date, "yyyy-MM-dd");
    } catch {
      return dateString;
    }
  };

  const formatTime = (dateString: string) => {
    try {
      const date = new Date(dateString);
      return format(date, "h:mm a");
    } catch {
      return "";
    }
  };

  const formatReceiptId = (receiptNo: number, receiptSer: string) => {
    if (receiptSer) {
      return `${receiptSer}-${receiptNo}`;
    }
    return `RCP-${receiptNo}`;
  };

  // Calculate metrics
  const totalCollections = useMemo(() => {
    return filteredReceipts.length;
  }, [filteredReceipts]);

  const totalValue = useMemo(() => {
    return filteredReceipts.reduce((sum, receipt) => sum + receipt.receiptAmt, 0);
  }, [filteredReceipts]);

  const avgCollectionValue = useMemo(() => {
    if (filteredReceipts.length === 0) return 0;
    return totalValue / filteredReceipts.length;
  }, [filteredReceipts, totalValue]);

  const formatCurrency = (amount: number) => {
    return `₹${amount.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  };

  // Lakhs-only formatter for summary cards (Total Value, Avg Collection Value)
  const formatLakhsCompact = (amount: number): string => {
    const value = Number.isFinite(amount) ? amount : 0;
    const absValue = Math.abs(value);
    const sign = value < 0 ? '-' : '';
    const lakhs = absValue / 100000;
    return `${sign}₹${lakhs.toFixed(2)} L`;
  };

  return (
    <Layout>
      <div className="flex flex-col h-[calc(100vh-8rem)] overflow-hidden">
        <div className="flex-shrink-0 space-y-3 pb-3 -mt-2">
          <div className="flex flex-col gap-3">
            <div className="flex items-center justify-between gap-4">
              <h2 className="text-3xl font-bold flex-shrink-0">Receipts</h2>
              <div className="relative max-w-sm flex-1 mt-2">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <Input
                  placeholder="Search receipts..."
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  className="pl-10"
                />
              </div>
            </div>
            <DateRangeFilter
              onFilterChange={handleDateFilterChange}
              onStatusChange={(newStatus) => setStatus(newStatus)}
            />
          </div>

          <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
            <StatCard
              title="Total Collections"
              value={isLoading ? <span className="text-muted-foreground animate-pulse">...</span> : totalCollections}
              icon={ClipboardCheck}
              variant="blue"
              className="animate-fade-up stagger-1"
            />
            <StatCard
              title="Total Value"
              value={isLoading ? <span className="text-muted-foreground animate-pulse">...</span> : formatLakhsCompact(totalValue)}
              icon={IndianRupee}
              variant="emerald"
              className="animate-fade-up stagger-2"
            />
            <StatCard
              title="Avg Collection Value"
              value={isLoading ? <span className="text-muted-foreground animate-pulse">...</span> : formatLakhsCompact(avgCollectionValue)}
              icon={TrendingUp}
              variant="violet"
              className="animate-fade-up stagger-3"
            />
          </div>
        </div>

       {/* Receipts List */}
       <div className="py-2 flex-1 overflow-hidden flex flex-col">
        {isLoading ? (
          <Card className="shadow-card">
            <CardContent className="p-8 text-center">
              <p className="text-muted-foreground">Loading receipts...</p>
            </CardContent>
          </Card>
        ) : filteredReceipts.length === 0 ? (
          <Card className="shadow-card">
            <CardContent className="p-8 text-center">
              <p className="mb-4 text-muted-foreground">No receipts found.</p>
            </CardContent>
          </Card>
        ) : (
          <Card className="shadow-card overflow-hidden bg-gradient-to-br from-sky-50/80 via-white to-indigo-50/60 dark:from-sky-950/30 dark:via-background dark:to-indigo-950/20">
            <div className="relative max-h-[400px] overflow-auto">
              <table className="w-full caption-bottom text-sm">
                <thead className="sticky top-0 z-10">
                  <tr className="border-b bg-muted">
                    <th className="min-w-[90px] py-2 px-3 text-left align-middle text-xs font-medium text-muted-foreground whitespace-nowrap">Receipt No</th>
                    <th className="min-w-[140px] py-2 px-3 text-left align-middle text-xs font-medium text-muted-foreground whitespace-nowrap">Customer</th>
                    <th className="min-w-[80px] py-2 px-2 text-left align-middle text-xs font-medium text-muted-foreground whitespace-nowrap">Town</th>
                    <th className="min-w-[100px] py-2 px-2 text-left align-middle text-xs font-medium text-muted-foreground whitespace-nowrap">Mobile No.</th>
                    <th className="min-w-[60px] py-2 px-2 text-left align-middle text-xs font-medium text-muted-foreground whitespace-nowrap">Mode</th>
                    <th className="min-w-[100px] py-2 px-2 text-left align-middle text-xs font-medium text-muted-foreground whitespace-nowrap">Date & Time</th>
                    <th className="min-w-[90px] py-2 px-3 text-right align-middle text-xs font-medium text-muted-foreground whitespace-nowrap">Amount</th>
                    <th className="min-w-[70px] py-2 px-2 text-right align-middle text-xs font-medium text-muted-foreground whitespace-nowrap">Discount</th>
                    <th className="min-w-[90px] py-2 px-3 text-right align-middle text-xs font-medium text-muted-foreground whitespace-nowrap">Net Amount</th>
                    <th className="min-w-[100px] py-2 px-3 text-left align-middle text-xs font-medium text-muted-foreground whitespace-nowrap">Narration</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredReceipts.map((receipt) => (
                    <tr
                      key={receipt.receiptNo}
                      className="border-b transition-colors hover:bg-muted/50"
                    >
                      <td className="px-3 py-2 align-middle whitespace-nowrap">
                        <span className="font-semibold text-primary text-sm">
                          RCP-{receipt.receiptNo}
                        </span>
                      </td>
                      <td className="px-3 py-2 align-middle">
                        <div className="flex items-center gap-1.5 whitespace-nowrap">
                          <span className="font-medium text-sm">{receipt.partyName}</span>
                          {receipt.partyCode && (
                            <>
                              <span className="text-xs text-muted-foreground">•</span>
                              <span className="text-xs text-muted-foreground">{receipt.partyCode}</span>
                            </>
                          )}
                        </div>
                      </td>
                      <td className="px-2 py-2 align-middle">
                        <span className="text-sm text-muted-foreground">
                          {receipt.city || "-"}
                        </span>
                      </td>
                      <td className="px-2 py-2 align-middle">
                        <span className="text-sm text-muted-foreground">
                          {receipt.mobileNumber || "-"}
                        </span>
                      </td>
                      <td className="px-2 py-2 align-middle">
                        <span className="text-sm whitespace-nowrap">
                          {receipt.mode}
                        </span>
                      </td>
                      <td className="px-2 py-2 align-middle">
                        <span className="text-sm whitespace-nowrap">
                          {formatDate(receipt.receiptDate)} {formatTime(receipt.receiptDate)}
                        </span>
                      </td>
                      <td className="px-3 py-2 align-middle text-right">
                        <span className="font-semibold text-primary text-sm">
                          {formatCurrency(receipt.receiptAmt)}
                        </span>
                      </td>
                      <td className="px-2 py-2 align-middle text-right">
                        <span className="text-sm whitespace-nowrap">
                          {formatCurrency(receipt.recDiscount)}
                        </span>
                      </td>
                      <td className="px-3 py-2 align-middle text-right">
                        <span className="font-semibold text-primary text-sm">
                          {formatCurrency(receipt.receiptAmt - receipt.recDiscount)}
                        </span>
                      </td>
                      <td className="px-3 py-2 align-middle max-w-[100px]">
                        <p
                          className="truncate text-xs text-muted-foreground cursor-pointer hover:text-primary hover:underline"
                          onClick={() => {
                            setSelectedNarration({
                              text: receipt.narration,
                              receiptNo: receipt.receiptNo,
                            });
                            setIsNarrationDialogOpen(true);
                          }}
                          title="Click to view full narration"
                        >
                          {receipt.narration.replace(/\\n/g, ' ')}
                        </p>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </Card>
        )}
      </div>
      </div>

      {/* Narration Dialog */}
      <Dialog open={isNarrationDialogOpen} onOpenChange={setIsNarrationDialogOpen}>
        <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>
              Receipt Narration - RCP-{selectedNarration?.receiptNo}
            </DialogTitle>
            <DialogDescription>
              Full narration details for this receipt
            </DialogDescription>
          </DialogHeader>
          <div className="mt-4">
            <div className="rounded-lg border bg-muted/50 p-4">
              <p className="whitespace-pre-wrap break-words text-sm">
                {selectedNarration?.text.replace(/\\n/g, '\n')}
              </p>
            </div>
          </div>
          <div className="mt-4 flex justify-end">
            <Button
              variant="outline"
              onClick={() => setIsNarrationDialogOpen(false)}
            >
              Close
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </Layout>
  );
};

export default Receipts;
